"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.Modal = void 0;
var _classnames = _interopRequireDefault(require("classnames"));
var _react = _interopRequireWildcard(require("react"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * This is a [React](https://reactjs.org/) component for the Vanilla [Modal](https://docs.vanillaframework.io/patterns/modal/).
 *
 * The modal component can be used to overlay an area of the screen which can contain a prompt, dialog or interaction.
 */
const Modal = _ref => {
  let {
    buttonRow,
    children,
    className,
    close,
    title,
    shouldPropagateClickEvent = false,
    ...wrapperProps
  } = _ref;
  // list of focusable selectors is based on this Stack Overflow answer:
  // https://stackoverflow.com/a/30753870/3732840
  const focusableElementSelectors = 'a[href]:not([tabindex="-1"]), button:not([disabled]), textarea:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), area[href]:not([tabindex="-1"]), iframe:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), [contentEditable=true]:not([tabindex="-1"])';
  const descriptionId = (0, _react.useId)();
  const titleId = (0, _react.useId)();
  const shouldClose = (0, _react.useRef)(false);
  const modalRef = (0, _react.useRef)(null);
  const focusableModalElements = (0, _react.useRef)(null);
  const handleTabKey = event => {
    if (focusableModalElements.current.length > 0) {
      const firstElement = focusableModalElements.current[0];
      const lastElement = focusableModalElements.current[focusableModalElements.current.length - 1];
      if (!event.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
      if (event.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        return event.preventDefault();
      }
    }
  };
  const handleEscKey = event => {
    if ("nativeEvent" in event && event.nativeEvent.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation();
    } else if ("stopImmediatePropagation" in event) {
      event.stopImmediatePropagation();
    } else if (event.stopPropagation) {
      event.stopPropagation();
    }
    if (close) {
      close();
    }
  };
  const keyListenersMap = new Map([["Escape", handleEscKey], ["Tab", handleTabKey]]);
  (0, _react.useEffect)(() => {
    modalRef.current.focus();
  }, [modalRef]);
  (0, _react.useEffect)(() => {
    var _focusableModalElemen;
    focusableModalElements.current = modalRef.current.querySelectorAll(focusableElementSelectors);
    let focusIndex = 0;
    // when the close button is rendered, focus on the 2nd content element and not the close btn.
    if (close && focusableModalElements.current.length > 1) {
      focusIndex = 1;
    }
    (_focusableModalElemen = focusableModalElements.current[focusIndex]) === null || _focusableModalElemen === void 0 || _focusableModalElemen.focus({
      preventScroll: true
    });
  }, [close]);
  (0, _react.useEffect)(() => {
    const keyDown = event => {
      const listener = keyListenersMap.get(event.code);
      return listener && listener(event);
    };
    document.addEventListener("keydown", keyDown);
    return () => {
      document.removeEventListener("keydown", keyDown);
    };
  });
  const handleContentOnMouseDown = () => {
    shouldClose.current = false;
  };
  const handleContentOnMouseUp = () => {
    shouldClose.current = false;
  };
  const handleOverlayOnMouseDown = event => {
    if (event.target === modalRef.current) {
      shouldClose.current = true;
    }
  };
  const handleClose = event => {
    if (!shouldPropagateClickEvent) {
      event.stopPropagation();
    }
    if (close) {
      close();
    }
  };
  const handleOverlayOnClick = event => {
    if (shouldClose.current) {
      handleClose(event);
    }
  };
  return /*#__PURE__*/_react.default.createElement("div", _extends({
    className: (0, _classnames.default)("p-modal", className),
    onClick: handleOverlayOnClick,
    onMouseDown: handleOverlayOnMouseDown
  }, wrapperProps, {
    ref: modalRef
  }), /*#__PURE__*/_react.default.createElement("section", {
    className: "p-modal__dialog",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": titleId,
    "aria-describedby": descriptionId,
    onMouseDown: handleContentOnMouseDown,
    onMouseUp: handleContentOnMouseUp
  }, !!title && /*#__PURE__*/_react.default.createElement("header", {
    className: "p-modal__header"
  }, /*#__PURE__*/_react.default.createElement("h2", {
    className: "p-modal__title",
    id: titleId
  }, title), !!close && /*#__PURE__*/_react.default.createElement("button", {
    className: "p-modal__close",
    "aria-label": "Close active modal",
    onClick: handleClose
  }, "Close")), /*#__PURE__*/_react.default.createElement("div", {
    id: descriptionId
  }, children), !!buttonRow && /*#__PURE__*/_react.default.createElement("footer", {
    className: "p-modal__footer"
  }, buttonRow)));
};
exports.Modal = Modal;
var _default = exports.default = Modal;